import { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { trackDashboardCtaClicked } from "analytics";
import type { CtaLocation } from "analytics/types";
import { INACTIVE_PLAN_REACTIVATION_FLOW_DISABLED } from "components/v3/pages/account/PlanInformationPage/InactivePlanCard";
import { PATHS } from "constants/pathConstants";
import { selectUserPersonalInformation } from "store/reducers/user/UserAccountSlice";
import {
  getTellOthersCopy,
  getTellOthersEligibility,
  shouldShowTellOthersGenomicLifeCard
} from "./tellOthersGenomicLifeUtils";

type UseTellOthersShareFlowOptions = {
  ctaLocation?: CtaLocation;
  onOpenCobraModal?: () => void;
};

export function useTellOthersShareFlow({
  ctaLocation,
  onOpenCobraModal
}: UseTellOthersShareFlowOptions = {}) {
  const history = useHistory();
  const userInformation = useSelector(selectUserPersonalInformation);
  const isMemberActive = userInformation.isMemberActive !== false;
  const eligibility = getTellOthersEligibility(
    userInformation.eligibleToGift,
    userInformation.eligibleToInvite
  );
  const copy = getTellOthersCopy(isMemberActive, eligibility);

  const [shareOptionsModalOpen, setShareOptionsModalOpen] = useState(false);

  const shouldShow =
    shouldShowTellOthersGenomicLifeCard(eligibility) &&
    (isMemberActive || !INACTIVE_PLAN_REACTIVATION_FLOW_DISABLED);

  const handlePrimaryClick = () => {
    if (ctaLocation) {
      trackDashboardCtaClicked({
        workflow: "account",
        ctaLocation,
        ctaId: "share_genomic_life"
      });
    }

    if (!isMemberActive) {
      onOpenCobraModal?.();
      return;
    }
    if (copy.primaryAction === "modal") {
      setShareOptionsModalOpen(true);
      return;
    }
    if (copy.primaryAction === "gift") {
      history.push(PATHS.ACCOUNT_MANAGEMENT_GIFT_MEMBERSHIP);
      return;
    }
    history.push(PATHS.ACCOUNT_MANAGEMENT_MEMBERSHIP_INVITE);
  };

  return {
    copy,
    closeShareOptionsModal: () => setShareOptionsModalOpen(false),
    eligibility,
    handlePrimaryClick,
    isMemberActive,
    shareOptionsModalOpen,
    shouldShow
  };
}
